/**
 * Build Badge
 * Displays build information in a fixed footer badge
 */

import { VERSION } from "../shared/version.generated";

// Declare injected global from webpack DefinePlugin
declare const __API_BASE_URL__: string;

interface BackendVersionResponse {
  version: {
    appName: string;
    env: "dev" | "prod";
    gitSha: string;
    buildTime: string;
  };
}

/**
 * Format build time for display
 */
function formatBuildTime(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

/**
 * Fetch backend version information
 */
async function fetchBackendVersion(apiBaseUrl: string): Promise<{ env: string; sha: string; timeMs: number } | null> {
  const startTime = performance.now();
  try {
    const url = apiBaseUrl.replace(/\/$/, "") + "/version";
    const response = await fetch(url);
    const timeMs = Math.round(performance.now() - startTime);
    
    if (!response.ok) {
      return { env: "ERR", sha: response.status.toString(), timeMs };
    }
    
    const data = (await response.json()) as BackendVersionResponse;
    return {
      env: data.version.env,
      sha: data.version.gitSha,
      timeMs,
    };
  } catch (error) {
    const timeMs = Math.round(performance.now() - startTime);
    const errorMsg = error instanceof Error ? error.message : String(error);
    return { env: "ERR", sha: errorMsg.substring(0, 10), timeMs };
  }
}

/**
 * Render the build badge
 */
export async function renderBuildBadge(): Promise<void> {
  const badgeEl = document.getElementById("optlab-build-badge");
  if (!badgeEl) {
    return;
  }

  // Get API base URL (from webpack DefinePlugin)
  const apiBaseUrl = typeof __API_BASE_URL__ !== "undefined" ? __API_BASE_URL__ : "unknown";
  
  // Get hostname
  const hostname = typeof window !== "undefined" && window.location ? window.location.hostname : "unknown";

  // Format frontend info
  const feInfo = "Env " + VERSION.env + " FE " + VERSION.gitSha;
  const feTime = formatBuildTime(VERSION.buildTime);
  const hostInfo = "Host " + hostname;
  const apiInfo = "API " + apiBaseUrl;

  // Render initial content (synchronously)
  badgeEl.innerHTML = 
    "<code>" + feInfo + "</code> " +
    "<code>" + hostInfo + "</code> " +
    "<code>" + apiInfo + "</code> " +
    "<code>BE Loading...</code>";

  // Fetch backend version asynchronously
  try {
    const backendInfo = await fetchBackendVersion(apiBaseUrl);
    if (backendInfo) {
      const beInfo = "BE " + backendInfo.env + ":" + backendInfo.sha + " (" + backendInfo.timeMs + "ms)";
      badgeEl.innerHTML = 
        "<code>" + feInfo + "</code> " +
        "<code>" + hostInfo + "</code> " +
        "<code>" + apiInfo + "</code> " +
        "<code>" + beInfo + "</code>";
    } else {
      badgeEl.innerHTML = 
        "<code>" + feInfo + "</code> " +
        "<code>" + hostInfo + "</code> " +
        "<code>" + apiInfo + "</code> " +
        "<code>BE ERR</code>";
    }
  } catch (error) {
    badgeEl.innerHTML = 
      "<code>" + feInfo + "</code> " +
      "<code>" + hostInfo + "</code> " +
      "<code>" + apiInfo + "</code> " +
      "<code>BE ERR</code>";
  }
}
